import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Mail, Lock, User, Eye, EyeOff, AtSign, Check, X } from "lucide-react";
import { z } from "zod";
import { useDespia } from "@/hooks/useDespia";
import { useToast } from "@/hooks/use-toast";
import TikTokLoginButton from "@/components/TikTokLoginButton";
import XLoginButton from "@/components/XLoginButton";
import { isTikTokCallback, validateOAuthState as validateTikTokState, exchangeTikTokCode, setTikTokSession } from "@/lib/tiktok-auth";
import { isXCallback, validateXOAuthState, exchangeXCode, setXSession } from "@/lib/x-auth";
import { sanitizeOAuthParams } from "@/lib/urlSanitizer";
import { recoverPendingSession } from "@/lib/oauthManager";

const emailSchema    = z.string().email("Invalid email address");
const passwordSchema = z.string().min(6, "Password must be at least 6 characters");

// FIX: Track the specific URL whose OAuth params we already processed.
// Previously a boolean flag (`_oauthHandlerFired = true`) blocked ALL future
// mounts from running the handler — even when the user returned from a NEW
// OAuth attempt with fresh tokens in the URL. By storing the processed URL
// we only skip duplicate processing for the SAME callback, not new ones.
let _lastProcessedOAuthUrl: string | null = null;

const Auth = () => {
  const navigate       = useNavigate();
  const [searchParams] = useSearchParams();
  const despia         = useDespia();
  const { toast }      = useToast();

  const [isLogin,      setIsLogin]      = useState(true);
  const [loading,      setLoading]      = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [oauthError,   setOauthError]   = useState<string | null>(null);
  const [authError,    setAuthError]    = useState<string | null>(null);

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  // FIX: Track when an OAuth exchange is actively in progress so the
  // onAuthStateChange listener does not race against the OAuth handler
  // and navigate to /home a second time while the handler is still running.
  const isProcessingOAuthRef = useRef(false);

  const [email,          setEmail]          = useState("");
  const [password,       setPassword]       = useState("");
  const [firstName,      setFirstName]      = useState("");
  const [lastName,       setLastName]       = useState("");
  const [username,       setUsername]       = useState("");
  const [usernameStatus, setUsernameStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');
  const usernameCheckTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Username availability check ─────────────────────────────────────────────
  const checkUsernameAvailability = useCallback(async (usernameToCheck: string) => {
    if (!usernameToCheck || usernameToCheck.length < 3) {
      setUsernameStatus('idle');
      return;
    }
    setUsernameStatus('checking');
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('username')
        .eq('username', usernameToCheck)
        .maybeSingle();
      if (error) { console.error('Username check error:', error); setUsernameStatus('idle'); return; }
      if (isMountedRef.current) setUsernameStatus(data ? 'taken' : 'available');
    } catch (err) {
      console.error('Username check failed:', err);
      if (isMountedRef.current) setUsernameStatus('idle');
    }
  }, []);

  const handleUsernameChange = (value: string) => {
    const sanitized = value.toLowerCase().replace(/[^a-z0-9_]/g, '');
    setUsername(sanitized);
    setUsernameStatus('idle');
    if (usernameCheckTimeout.current) clearTimeout(usernameCheckTimeout.current);
    if (sanitized.length >= 3) {
      usernameCheckTimeout.current = setTimeout(() => checkUsernameAvailability(sanitized), 500);
    }
  };

  useEffect(() => {
    return () => { if (usernameCheckTimeout.current) clearTimeout(usernameCheckTimeout.current); };
  }, []);

  // ── OAuth token / callback handler ──────────────────────────────────────────
  useEffect(() => {
    const currentUrl = window.location.href;
    if (_lastProcessedOAuthUrl === currentUrl) return;
    _lastProcessedOAuthUrl = currentUrl;

    const handleOAuthTokens = async () => {
      console.log('[Auth] handleOAuthTokens called, URL:', window.location.href);

      const hash         = window.location.hash.substring(1);
      const hashParams   = new URLSearchParams(hash);
      const windowParams = new URLSearchParams(window.location.search);

      // FIX: Only attempt session recovery and OAuth processing when the URL
      // actually contains OAuth parameters. Previously recoverPendingSession()
      // ran on every Auth page mount — adding network latency on every normal visit.
      const hasOAuthParams =
        hashParams.has('access_token') ||
        windowParams.has('access_token') ||
        searchParams.has('access_token') ||
        windowParams.has('code') ||
        searchParams.has('code') ||
        hashParams.has('error') ||
        windowParams.has('error');

      if (!hasOAuthParams) {
        console.log('[Auth] No OAuth params detected — skipping handler');
        return;
      }

      // Try to recover a pending session from a failed deeplink
      const recovered = await recoverPendingSession();
      if (recovered) {
        console.log('[Auth] Recovered pending session from storage');
        if (!isMountedRef.current) return;
        despia.haptic.success();
        navigate('/home', { replace: true });
        return;
      }

      console.log('[Auth] Token sources — hash:', hash ? 'present' : 'empty',
        'search:', window.location.search ? 'present' : 'empty',
        'access_token in hash:', hashParams.has('access_token'),
        'access_token in search:', windowParams.has('access_token') || searchParams.has('access_token'));

      // OAuth error from provider
      const errorParam       = searchParams.get('error')       || hashParams.get('error')       || windowParams.get('error');
      const errorDescription = searchParams.get('error_description') || hashParams.get('error_description') || windowParams.get('error_description');
      if (errorParam) {
        console.error('[Auth] OAuth error:', errorParam, errorDescription);
        if (isMountedRef.current) setOauthError(decodeURIComponent(errorDescription || errorParam));
        sanitizeOAuthParams();
        return;
      }

      // X (Twitter) callback
      if (isXCallback(windowParams)) {
        console.log('[Auth] X OAuth callback detected');
        const code        = windowParams.get('code')!;
        const state       = windowParams.get('state')!;
        const stateResult = validateXOAuthState(state);
        if (stateResult.warning) console.warn('[Auth] X state validation warning:', stateResult.warning);
        try {
          if (!isMountedRef.current) return;
          isProcessingOAuthRef.current = true;
          setLoading(true);
          if (!stateResult.codeVerifier) throw new Error('Missing code verifier for PKCE flow');
          console.log('[Auth] Exchanging X code, isNative:', stateResult.isNative, 'storedRedirectUri:', stateResult.redirectUri);
          const tokens = await exchangeXCode(code, stateResult.codeVerifier, stateResult.isNative, stateResult.redirectUri);
          await setXSession(tokens.access_token, tokens.refresh_token);
          console.log('[Auth] X session set successfully');
          sanitizeOAuthParams();
          if (!isMountedRef.current) return;
          despia.haptic.success();
          navigate('/home', { replace: true });
        } catch (err) {
          console.error('[Auth] X auth failed:', err);
          sanitizeOAuthParams();
          if (isMountedRef.current) {
            setOauthError(err instanceof Error ? err.message : 'X sign in failed');
            setLoading(false);
          }
        } finally {
          isProcessingOAuthRef.current = false;
        }
        return;
      }

      // TikTok callback
      if (isTikTokCallback(windowParams)) {
        console.log('[Auth] TikTok OAuth callback detected');
        const code        = windowParams.get('code')!;
        const state       = windowParams.get('state')!;
        const stateResult = validateTikTokState(state);
        if (stateResult.warning) console.warn('[Auth] TikTok state validation warning:', stateResult.warning);
        try {
          if (!isMountedRef.current) return;
          isProcessingOAuthRef.current = true;
          setLoading(true);
          console.log('[Auth] Exchanging TikTok code, isNative:', stateResult.isNative, 'storedRedirectUri:', stateResult.redirectUri);
          const tokens = await exchangeTikTokCode(code, stateResult.isNative, stateResult.redirectUri);
          await setTikTokSession(tokens.access_token, tokens.refresh_token);
          console.log('[Auth] TikTok session set successfully');
          sanitizeOAuthParams();
          if (!isMountedRef.current) return;
          despia.haptic.success();
          navigate('/home', { replace: true });
        } catch (err) {
          console.error('[Auth] TikTok auth failed:', err);
          sanitizeOAuthParams();
          if (isMountedRef.current) {
            setOauthError(err instanceof Error ? err.message : 'TikTok sign in failed');
            setLoading(false);
          }
        } finally {
          isProcessingOAuthRef.current = false;
        }
        return;
      }

      // Google / generic OAuth — access_token in hash or query params
      const accessToken  = hashParams.get('access_token')  || searchParams.get('access_token')  || windowParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token') || searchParams.get('refresh_token') || windowParams.get('refresh_token');

      if (accessToken) {
        console.log('[Auth] Found OAuth tokens, setting session');
        isProcessingOAuthRef.current = true;
        try {
          const { error: sessionError } = await supabase.auth.setSession({
            access_token:  accessToken,
            refresh_token: refreshToken || '',
          });
          if (sessionError) {
            console.error('[Auth] Session error:', sessionError);
            if (isMountedRef.current) setOauthError(sessionError.message);
            sanitizeOAuthParams();
            return;
          }
          console.log('[Auth] Session set successfully, redirecting to home');
          sanitizeOAuthParams();
          if (!isMountedRef.current) return;
          despia.haptic.success();
          navigate('/home', { replace: true });
        } catch (err) {
          console.error('[Auth] Failed to set session:', err);
          if (isMountedRef.current) setOauthError(err instanceof Error ? err.message : 'Failed to complete sign in');
          sanitizeOAuthParams();
        } finally {
          isProcessingOAuthRef.current = false;
        }
      }
    };

    handleOAuthTokens();
  }, [searchParams, navigate, despia]);

  // MAINTENANCE MODE: Disable session auto-redirect — keep everyone on this page
  useEffect(() => {
    // No-op: all users should see the under-construction banner
  }, [navigate]);

  // ── Form validation ─────────────────────────────────────────────────────────
  const validateInputs = (): boolean => {
    try {
      emailSchema.parse(email);
    } catch (error) {
      if (error instanceof z.ZodError) {
        despia.haptic.warning();
        toast({ title: "Invalid email", description: error.issues[0].message, variant: "destructive" });
      }
      return false;
    }
    try {
      passwordSchema.parse(password);
    } catch (error) {
      if (error instanceof z.ZodError) {
        despia.haptic.warning();
        toast({ title: "Password too short", description: error.issues[0].message, variant: "destructive" });
      }
      return false;
    }
    if (!isLogin) {
      if (!firstName.trim()) {
        despia.haptic.warning();
        toast({ title: "First name required", description: "Please enter your first name", variant: "destructive" });
        return false;
      }
      if (!lastName.trim()) {
        despia.haptic.warning();
        toast({ title: "Last name required", description: "Please enter your last name", variant: "destructive" });
        return false;
      }
      if (!username.trim()) {
        despia.haptic.warning();
        toast({ title: "Username required", description: "Please choose a username", variant: "destructive" });
        return false;
      }
      if (!/^[a-zA-Z0-9_]+$/.test(username)) {
        despia.haptic.warning();
        toast({ title: "Invalid username", description: "Username can only contain letters, numbers, and underscores", variant: "destructive" });
        return false;
      }
      if (usernameStatus === 'checking') {
        toast({ title: "Checking username", description: "Please wait a moment then try again", variant: "destructive" });
        return false;
      }
      if (usernameStatus === 'taken') {
        despia.haptic.warning();
        toast({ title: "Username taken", description: "Please choose a different username", variant: "destructive" });
        return false;
      }
    }
    return true;
  };

  // ── Sign up ─────────────────────────────────────────────────────────────────
  const handleSignUp = async () => {
    if (!validateInputs()) return;
    setLoading(true);
    setAuthError(null);

    const isNative    = navigator.userAgent.toLowerCase().includes('despia');
    const redirectUrl = isNative
      ? 'gistgift://auth'
      : `${window.location.origin}/auth/callback`;

    console.log('Sign up redirect URL:', redirectUrl, 'isNative:', isNative);

    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          first_name:   firstName,
          last_name:    lastName,
          username,
          display_name: `${firstName} ${lastName}`,
        },
      },
    });

    if (!isMountedRef.current) return;
    setLoading(false);

    if (error) {
      despia.haptic.error();
      // FIX: Show error in the inline banner only — not as a destructive toast.
      // Previously both setAuthError AND toast({variant:"destructive"}) fired
      // for the same error: a red popup AND a red banner simultaneously on mobile.
      // The banner persists so the user can read it; the toast disappears too fast.
      setAuthError(error.message);
    } else {
      despia.haptic.success();
      toast({
        title:       "Account created!",
        description: "Check your email to verify your account before signing in.",
      });
      setEmail(""); setPassword(""); setFirstName(""); setLastName(""); setUsername("");
      setUsernameStatus('idle');
      setIsLogin(true);
    }
  };

  // ── Sign in ─────────────────────────────────────────────────────────────────
  const handleSignIn = async () => {
    if (!validateInputs()) return;
    setLoading(true);
    setAuthError(null);

    const { data, error } = await supabase.auth.signInWithPassword({ email, password });

    if (!isMountedRef.current) return;
    setLoading(false);

    if (error) {
      despia.haptic.error();
      // FIX: Banner only — same as handleSignUp (no redundant destructive toast).
      setAuthError(error.message);
    } else {
      if (data.user && !data.user.email_confirmed_at) {
        await supabase.auth.signOut();
        if (!isMountedRef.current) return;
        const msg = "Please verify your email before signing in. Check your inbox for the verification link.";
        setAuthError(msg);
        toast({ title: "Email not verified", description: msg });
      } else {
        despia.haptic.success();
        toast({ title: "Welcome back!" });
      }
    }
  };

  // ── Mode switch ──────────────────────────────────────────────────────────────
  const switchMode = () => {
    setIsLogin(prev => !prev);
    setEmail(""); setPassword(""); setFirstName(""); setLastName(""); setUsername("");
    setUsernameStatus('idle');
    setAuthError(null);
    setOauthError(null);
  };

  // ── Continue as Guest ────────────────────────────────────────────────────────
  const handleContinueAsGuest = () => {
    // FIX: Write a guest-mode flag before navigating so that route guards and
    // auth-checking hooks elsewhere in the app know the user intentionally chose
    // to browse without signing in. Without this flag, any component that checks
    // "no session → redirect to /auth" boots the guest back to this page in an
    // endless redirect loop — the primary cause of guest users getting booted.
    // FIX: Persist guest mode in localStorage so it survives cold starts.
    // sessionStorage is volatile — killed on app close — which caused guest
    // users to lose deep-link eligibility and get redirected to /auth on relaunch.
    localStorage.setItem('gistgift_guest_mode', '1');
    sessionStorage.setItem('gistgift_guest_mode', '1'); // keep for backward compat
    navigate("/home");
  };

  // ── JSX ──────────────────────────────────────────────────────────────────────
  return (
    <div
      className="min-h-screen bg-gradient-secondary overflow-y-auto"
      style={{
        paddingTop:    'calc(1.5rem + var(--safe-area-top, 0px))',
        paddingBottom: 'calc(1.5rem + var(--safe-area-bottom, 0px))',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <div className="min-h-full flex items-center justify-center p-6">
      <div className="w-full max-w-md space-y-8 animate-fade-in">
        {/* Logo */}
        <div className="text-center">
          <img
            src="/logo.webp" alt="GistGift"
            className="w-20 h-20 mx-auto mb-4"
            onError={(e) => { (e.target as HTMLImageElement).src = '/logo.png'; }}
          />
          <h1 className="text-3xl font-bold text-foreground">
            GistGift
          </h1>
        </div>

        {/* Under Construction Banner */}
        <div className="bg-primary/5 border-2 border-primary/30 rounded-2xl p-6 text-center space-y-4">
          <div className="flex justify-center">
            <span className="text-4xl">🚧</span>
          </div>
          <h2 className="text-xl font-bold text-foreground">Under Construction</h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            We're currently working on resolving some issues that users have been experiencing. 
            The app is temporarily unavailable while we make improvements.
          </p>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Thank you for your patience — we'll be back up and running soon!
          </p>
          <div className="pt-2">
            <p className="text-xs text-muted-foreground">
              For questions, reach out to{" "}
              <a href="mailto:j01652285@gmail.com" className="text-primary hover:underline">
                GistGift Support
              </a>
            </p>
          </div>
        </div>

        <p className="text-xs text-center text-muted-foreground">
          <Link to="/terms-of-service" className="text-primary hover:underline">Terms of Service</Link>
          {" · "}
          <Link to="/privacy-policy" className="text-primary hover:underline">Privacy Policy</Link>
        </p>
      </div>
      </div>
    </div>
  );
};

export default Auth;
